import * as Sentry from '@sentry/react';

export enum StorageKeys {
  IMPERSONATION_EMAIL = 'impersonation_email',
  IMPERSONATION_ID = 'impersonation_id',

  VOA_FANNIE_FREDDIE_POPUP = 'promo_voa_fannie_freddie_popup',
  VOA_FANNIE_FREDDIE_HEADER = 'promo_voa_fannie_freddie_header',

  SUBSCRIPTION_REDIRECT_COUNT = 'subscription_redirect_count',
  ONBOARDING_AUTO_OPENED = 'truv_onboarding_auto_opened',
  ONBOARDING_CHECKLIST_OPEN = 'truv_onboarding_checklist_open',
  SELECTED_PLAN = 'truv_selected_plan',
  SIGNUP_SOURCE = 'truv_signup_source',
  SIGNUP_USE_CASE = 'truv_signup_use_case',
  SIGNUP_VERIFICATION_VOLUME = 'truv_signup_verification_volume',

  // Integration
  WORKNUMBER = 'worknumber',
  EXPERIAN = 'experian',
  TRUEWORK = 'truework',
  OCROLUS = 'ocrolus',
  INFORMED = 'informed',
  SIMPLENEXUS = 'simplenexus',
  BANNO = 'banno',
  BLACKKNIGHT = 'blackknight',
  ACCIO = 'accio',
  ENCOMPASS = 'encompass',
  CONSTELLATION = 'constellation',
  TAZWORKS = 'tazworks',
  TWILIO = 'twilio',

  COMMON_FILTERS = 'common_filters',
  ORDER_MANAGER_FILTER = 'order_manager_filter',

  LAST_SHOWN_INCIDENT_ID = 'last_shown_incident_id',

  SIDEBAR_STATE = 'sidebar_state',

  // Upgrade promo (CL-6252)
  UPGRADE_PROMO_STATE = 'upgrade_promo_state',

  PROD_KEY_WATCH_STATE = 'prod_key_watch_state',

  VERIFICATION_DRAFT = 'truv_verification_draft',

  // DataGrid
  ORDERS_TABLE_COLUMNS = 'orders_table_columns',
  USERS_TABLE_COLUMNS = 'users_table_columns',
  BILLING_USAGE_TABLE_COLUMNS = 'billing_usage_table_columns',
  TASKS_TABLE_COLUMNS = 'tasks_table_columns',
  TEAM_MEMBERS_TABLE_COLUMNS = 'team_members_table_columns',
  TEMPLATES_TABLE_COLUMNS = 'templates_table_columns',
  LOGS_TABLE_COLUMNS = 'logs_table_columns',
  WEBHOOKS_TABLE_COLUMNS = 'webhooks_table_columns',
}

const handleStorageError = <T>(fn: () => T): T | undefined => {
  try {
    return fn();
  } catch (err) {
    console.error('localStorage error:', err);
    Sentry.captureException(err);

    return undefined;
  }
};

export const createStorageService = (storage: Storage) => ({
  get: (key: StorageKeys) =>
    handleStorageError(() => {
      const value = storage.getItem(key);

      if (!value) {
        return undefined;
      }

      try {
        return JSON.parse(value);
      } catch {
        // If JSON parsing fails, return the raw string value
        return value;
      }
    }),
  set: (key: StorageKeys, value: unknown) => handleStorageError(() => storage.setItem(key, JSON.stringify(value))),
  remove: (key: StorageKeys) => handleStorageError(() => storage.removeItem(key)),
});

export const localStorageService = createStorageService(localStorage);
export const sessionStorageService = createStorageService(sessionStorage);

export type StorageMigration<T> = {
  version: number;
  migrate: (state: T) => T;
};

// Sibling field on persisted state tracking the highest migration version applied.
export const STORAGE_VERSION_FIELD = '__v';

type VersionedState<T> = T & { [STORAGE_VERSION_FIELD]?: number };

type ReadMigratedStateParams<T> = {
  storageKey: StorageKeys;
  migrations: StorageMigration<T>[];
};

export const readMigratedState = <T>({ storageKey, migrations }: ReadMigratedStateParams<T>): T | undefined => {
  const raw = localStorageService.get(storageKey) as VersionedState<T> | undefined;
  if (!raw) {
    return undefined;
  }

  const { [STORAGE_VERSION_FIELD]: currentVersion = 0, ...rest } = raw;
  const state = rest as T;

  return migrations
    .filter((m) => m.version > currentVersion)
    .sort((a, b) => a.version - b.version)
    .reduce<T>((acc, m) => m.migrate(acc), state);
};
